import type {IPointData} from '@pixi/core';
import {Graphics, PixiRef} from '@pixi/react';
import {Sound} from '@pixi/sound';
import {Assets} from 'pixi.js';
import {useEffect, useMemo, useRef} from 'react';
import {RingSprite} from './RingSprite';
import {TRANSFER_RING} from '../assetManifest';
import {useAnimation} from '../hooks/useAnimation';
import {TransferRingEvent} from '../SupplyChainPayloadTypes';

export const TRANSFER_RING_DURATION_MS = 1000;

interface TransferRingAnimationProps {
    activeTransfer: TransferRingEvent;
    points: IPointData[];
}

function scale(y: number): number {
    return (y + 504) / 1080;
}

export function TransferRingAnimation({points, activeTransfer}: TransferRingAnimationProps) {
    const spriteRef = useRef<PixiRef<typeof Graphics>>(null);

    const diff = useMemo(() => (!points || points.length < 2) ? {x: 0, y: 0} : {
        x: points[points.length - 1].x - points[0].x,
        y: points[points.length - 1].y - points[0].y,
    }, [points]);

    useAnimation({
        animation: (progress: number) => {
            if (spriteRef && spriteRef.current) {
                const currScale = scale(points[0].y / 10 + (diff.y / 10 * progress));
                spriteRef.current.x = points[0].x / 10 + (diff.x / 10 * progress);
                spriteRef.current.y = points[0].y / 10 + (diff.y / 10 * progress);
                spriteRef.current.scale = {x: currScale, y: currScale};
            }
        }, animationTimeMs: TRANSFER_RING_DURATION_MS,
    }, [spriteRef, activeTransfer.ringId, points, activeTransfer]);

    useEffect(() => {
        void Assets.get<Sound>(TRANSFER_RING).play();
    }, []);

    return (
        <RingSprite innerRef={spriteRef} ringId={activeTransfer.ringId}
            x={points[0].x} y={points[0].y} scale={scale(points[0].y / 10)}/>
    );
}
